'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { Sun, Waves, Car, Users, Moon, Briefcase, Home, Star, ArrowRight, AlertCircle, Loader2, Sparkles } from 'lucide-react';

type PageState = 'loading' | 'populated' | 'unavailable';

const dayInLife = [
  {
    id: 'morning',
    time: 'Morning',
    icon: Sun,
    title: 'Morning Wellness & Fitness',
    description: 'Start the day in the fitness center with views that make the effort worthwhile. The pool deck is quiet in the early hours — a rare luxury in a city that rarely sleeps.',
    accent: '#4CC9FF',
  },
  {
    id: 'midday',
    time: 'Midday',
    icon: Waves,
    title: 'Pool, Spa & Recovery',
    description: 'The pool areas at Panorama are a genuine retreat. Lounge, swim, or use the spa facilities to reset. Midday at the pool feels like a resort — because it is one.',
    accent: '#7A5CFF',
  },
  {
    id: 'afternoon',
    time: 'Afternoon',
    icon: Briefcase,
    title: 'Work, Focus & Convenience',
    description: 'Whether you work from home or use Panorama as a base between travel, the building supports a productive afternoon. Concierge handles logistics. You handle the work.',
    accent: '#4CC9FF',
  },
  {
    id: 'evening-prep',
    time: 'Early Evening',
    icon: Car,
    title: 'Valet Convenience',
    description: 'Heading out? Valet handles the car. No parking stress, no circling the garage. It\'s a small thing that makes a meaningful difference in daily quality of life.',
    accent: '#FF3DF5',
  },
  {
    id: 'entertaining',
    time: 'Evening',
    icon: Users,
    title: 'Easy Entertaining',
    description: 'Panorama\'s common areas and the building\'s overall aesthetic make it a natural setting for hosting. Invite neighbors, bring guests, and let the building do the work.',
    accent: '#7A5CFF',
  },
  {
    id: 'night',
    time: 'Night',
    icon: Moon,
    title: 'Evening Access to Las Vegas',
    description: 'The Strip is minutes away. T-Mobile Arena, the Sphere, world-class dining, and nightlife — all within reach. Come home to a building that feels like a reward.',
    accent: '#FF3DF5',
  },
];

const lifestyleHighlights = [
  {
    id: 'lock-leave',
    icon: Home,
    title: 'Lock-and-Leave Living',
    body: 'Panorama is designed for residents who travel. The building staff, concierge team, and community culture make it genuinely easy to be away — and genuinely good to return.',
    accent: '#4CC9FF',
  },
  {
    id: 'work-play',
    icon: Briefcase,
    title: 'A Home Base for Work, Play & Travel',
    body: 'Whether you\'re a full-time resident, a frequent traveler, or a second-home owner, Panorama functions as a premium base of operations. Everything you need is either in the building or minutes away.',
    accent: '#7A5CFF',
  },
  {
    id: 'wellness',
    icon: Sparkles,
    title: 'Wellness Built In',
    body: 'Fitness center, pool, spa, and massage rooms — the wellness infrastructure at Panorama is not an afterthought. It\'s part of the daily rhythm for residents who prioritize it.',
    accent: '#FF3DF5',
  },
];

const communityOutings = [
  { label: 'Golden Knights Games', icon: '🏒' },
  { label: 'Raiders Games', icon: '🏈' },
  { label: 'Sphere Events', icon: '🌐' },
  { label: 'Conventions & Expos', icon: '🎪' },
  { label: 'Dining Outings', icon: '🍽️' },
  { label: 'Nightlife & Concerts', icon: '🎶' },
  { label: 'Social Meetups', icon: '🥂' },
  { label: 'Weekend Excursions', icon: '🚗' },
];

export default function LifestylePage() {
  const [pageState, setPageState] = useState<PageState>('loading');

  useEffect(() => {
    const t = setTimeout(() => setPageState('populated'), 850);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="transparent" />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden panorama-hero-bg">
          <div
            className="absolute top-0 right-0 w-[55%] h-[75%] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 70% 65% at 100% 5%, rgba(122,92,255,0.18) 0%, rgba(76,201,255,0.08) 50%, transparent 70%)' }}
          />
          <div
            className="absolute bottom-0 left-0 w-[45%] h-[50%] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 80% 70% at 0% 100%, rgba(255,61,245,0.10) 0%, transparent 65%)' }}
          />
          <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-6"
              style={{ borderRadius: '6px', background: 'rgba(255,61,245,0.10)', border: '1px solid rgba(255,61,245,0.22)' }}
            >
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#FF3DF5' }} />
              <span className="text-[12px] font-medium tracking-wide" style={{ color: '#FF3DF5' }}>
                The Panorama Lifestyle
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1
                  className="font-display font-extrabold text-[48px] sm:text-[60px] lg:text-[68px] leading-[1.04] tracking-tight mb-5"
                  style={{ color: '#F2F4FF' }}
                >
                  This is what{' '}
                  <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #FF3DF5 0%, #7A5CFF 50%, #4CC9FF 100%)' }}>
                    living here
                  </span>{' '}
                  actually feels like.
                </h1>
                <p className="text-[18px] leading-relaxed max-w-2xl mb-10 font-light" style={{ color: 'rgba(242,244,255,0.72)' }}>
                  Panorama is not just an address. It's a daily experience — one that blends premium amenities, genuine community, and the full energy of Las Vegas, all from a building that feels like a retreat.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link href="/request-access" className="btn-primary group">
                    Request Access
                    <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Link>
                  <Link href="/amenities" className="btn-secondary">
                    See Amenities
                  </Link>
                </div>
              </div>
              {/* Hero image placeholder */}
              <div
                className="hidden lg:flex h-[420px] rounded-xl flex-col items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,61,245,0.07) 0%, rgba(122,92,255,0.09) 50%, rgba(76,201,255,0.06) 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(167,163,196,0.30)" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="m21 15-5-5L5 21" />
                  </svg>
                </div>
                <p className="text-[13px] font-medium" style={{ color: 'rgba(167,163,196,0.40)' }}>Lifestyle photography coming soon</p>
                <p className="text-[11px] mt-1" style={{ color: 'rgba(167,163,196,0.25)' }}>Pool deck, views, and resident life</p>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 neon-divider" />
        </section>

        {/* Loading */}
        {pageState === 'loading' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 size={32} className="animate-spin" style={{ color: '#FF3DF5' }} />
              <p className="text-[15px]" style={{ color: '#A7A3C4' }}>Loading…</p>
            </div>
          </section>
        )}

        {/* Unavailable */}
        {pageState === 'unavailable' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,61,245,0.10)', border: '1px solid rgba(255,61,245,0.20)' }}>
                <AlertCircle size={22} style={{ color: '#FF3DF5' }} />
              </div>
              <h3 className="font-display font-semibold text-[20px]" style={{ color: '#F2F4FF' }}>Content unavailable</h3>
              <p className="text-[14px] max-w-sm" style={{ color: '#A7A3C4' }}>We're having trouble loading this page. Please try again shortly.</p>
              <Link href="/" className="btn-secondary text-[13px] px-4 py-2 mt-2">Return Home</Link>
            </div>
          </section>
        )}

        {/* Populated */}
        {pageState === 'populated' && (
          <>
            {/* A Day at Panorama */}
            <section className="py-20" style={{ background: '#0C0A16' }}>
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
                <div className="text-center mb-14">
                  <p className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3" style={{ color: '#4CC9FF' }}>
                    A Day at Panorama
                  </p>
                  <h2 className="font-display font-bold text-[36px] sm:text-[42px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                    From morning wellness to evening Las Vegas.
                  </h2>
                  <p className="text-[16px] max-w-xl mx-auto" style={{ color: '#A7A3C4' }}>
                    Every day at Panorama has a rhythm. Here's what it looks like.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {dayInLife.map((item) => (
                    <div key={item.id} className="neon-card p-7">
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ background: `${item.accent}12`, border: `1px solid ${item.accent}22` }}
                        >
                          <item.icon size={18} style={{ color: item.accent }} />
                        </div>
                        <span className="text-[11px] font-semibold tracking-wide uppercase" style={{ color: item.accent }}>
                          {item.time}
                        </span>
                      </div>
                      <h3 className="font-display font-semibold text-[17px] mb-3" style={{ color: '#F2F4FF' }}>
                        {item.title}
                      </h3>
                      <p className="text-[13.5px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Lifestyle highlights */}
            <section className="py-20" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {lifestyleHighlights.map((h) => (
                    <div key={h.id} className="neon-card p-7">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                        style={{ background: `${h.accent}12`, border: `1px solid ${h.accent}22` }}
                      >
                        <h.icon size={18} style={{ color: h.accent }} />
                      </div>
                      <h3 className="font-display font-semibold text-[17px] mb-3" style={{ color: '#F2F4FF' }}>
                        {h.title}
                      </h3>
                      <p className="text-[13.5px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                        {h.body}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Community outings */}
            <section className="py-20" style={{ background: '#0C0A16' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <div>
                    <p className="text-[12px] font-semibold tracking-[0.12em] uppercase mb-3" style={{ color: '#7A5CFF' }}>
                      Community Outings
                    </p>
                    <h2 className="font-display font-bold text-[36px] sm:text-[42px] leading-tight tracking-tight mb-5" style={{ color: '#F2F4FF' }}>
                      There's always something going on.
                    </h2>
                    <p className="text-[16px] leading-relaxed mb-5" style={{ color: 'rgba(167,163,196,0.90)' }}>
                      Panorama residents don't just live in the same building — they go places together. Golden Knights games, Raiders games, Sphere events, conventions, dining outings, and late-night social plans are part of the culture here.
                    </p>
                    <p className="text-[15px] leading-relaxed mb-8" style={{ color: 'rgba(167,163,196,0.75)' }}>
                      The private community platform lets residents coordinate plans, invite neighbors along, and make connections around shared interests. It's not organized — it's organic. And it works.
                    </p>
                    <Link href="/community" className="btn-secondary">
                      Explore the Community
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {communityOutings.map((outing) => (
                      <div
                        key={outing.label}
                        className="neon-card p-4 flex items-center gap-3"
                      >
                        <span className="text-[22px]">{outing.icon}</span>
                        <span className="text-[13.5px] font-medium" style={{ color: '#F2F4FF' }}>{outing.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Escalade section */}
            <section className="py-20" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20">
                <div
                  className="relative overflow-hidden panorama-premium-bg rounded-xl p-10 lg:p-14"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <div className="absolute top-0 right-0 w-[50%] h-full pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 70% at 100% 20%, rgba(76,201,255,0.12) 0%, transparent 65%)' }} />
                  <div className="relative z-10 max-w-2xl">
                    <div
                      className="inline-flex items-center gap-2 px-3 py-1.5 mb-6"
                      style={{ borderRadius: '6px', background: 'rgba(76,201,255,0.10)', border: '1px solid rgba(76,201,255,0.22)' }}
                    >
                      <Star size={12} style={{ color: '#4CC9FF' }} />
                      <span className="text-[12px] font-medium tracking-wide" style={{ color: '#4CC9FF' }}>
                        Part of the Panorama Experience
                      </span>
                    </div>
                    <h2 className="font-display font-bold text-[32px] sm:text-[38px] leading-tight tracking-tight mb-5" style={{ color: '#F2F4FF' }}>
                      The Escalades are part of the story.
                    </h2>
                    <p className="text-[16px] leading-relaxed mb-4" style={{ color: 'rgba(167,163,196,0.90)' }}>
                      Panorama is home to two Cadillac Escalade limos — a reflection of the kind of lifestyle and social energy that defines the community. When residents head out together, they do it in style.
                    </p>
                    <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'rgba(167,163,196,0.75)' }}>
                      Whether it's a group outing to a Golden Knights game, a Sphere event, or a night on the Strip, the Escalades are part of what makes Panorama feel like more than a building. They're a symbol of the premium, community-oriented culture here.
                    </p>
                    <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.50)' }}>
                      Access the private resident platform to connect with neighbors and learn more about community culture and outings.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-20" style={{ background: '#0C0A16' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20 text-center">
                <h2 className="font-display font-bold text-[32px] sm:text-[40px] leading-tight tracking-tight mb-4" style={{ color: '#F2F4FF' }}>
                  Ready to experience Panorama living?
                </h2>
                <p className="text-[16px] max-w-xl mx-auto mb-8" style={{ color: '#A7A3C4' }}>
                  Request access to the private resident community or explore real estate opportunities at Panorama Towers.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Link href="/request-access" className="btn-primary group">
                    Request Access
                    <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Link>
                  <Link href="/real-estate" className="btn-secondary">
                    Explore Real Estate
                  </Link>
                </div>

                {/* Cross-links */}
                <div
                  className="mt-14 pt-10 border-t"
                  style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                >
                  <p className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-5" style={{ color: 'rgba(167,163,196,0.35)' }}>
                    Explore more
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <Link href="/why-panorama" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Why Panorama
                    </Link>
                    <Link href="/amenities" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Amenities
                    </Link>
                    <Link href="/community" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Community
                    </Link>
                    <Link href="/nearby" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      What's Nearby
                    </Link>
                    <Link href="/faq" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      FAQ
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
